//
// Copyright (c) 2025 Comcast
//

import { api, uploadWithProgressQuery } from './api'
import {
  updateAttachmentUploadProgress,
  abortAttachmentUpload,
} from 'reducers/ticket'


export const ticketingSystemTicketType = 'ticketingSystemTicket'

const enhancedApi = api.enhanceEndpoints({
  addTagTypes: [
    ticketingSystemTicketType,
    'ticketingSystemTicketAttachments',
    'ticketingSystemTicketComments',
  ],
})

const getPartnerStatusesMap = (response) => {
  const partnerStatusesMap = {}
  const partnerStatuses = response?.partnerStatuses || []
  partnerStatuses.forEach(status => {
    partnerStatusesMap[status.name] = { color: status.color }
  })

  return partnerStatusesMap
}

const uploadAborter = {
  uploadAbortFunction: null,

  uploadAbortCallback (func) {
    uploadAborter.uploadAbortFunction = func
  },

  abortUpload () {
    uploadAborter.uploadAbortFunction?.()
    uploadAborter.uploadAbortFunction = null
  },
}

// manually update ticket cache to prevent additional API fetch request
const manuallyUpdateTicketCache = async function ({ ticketingSystemDisplayId, ticketId }, { dispatch, queryFulfilled }) {
  try {
    const { data } = await queryFulfilled

    if (data.error) {
      return
    }

    dispatch(
      api.util.updateQueryData(
        'fetchTicket',
        { ticketingSystemDisplayId, ticketId },
        () => data
      )
    )
  } catch {}
}

// update only subscription field in cache
export const manuallyUpdateTicketCacheSubscription = async function ({ ticketingSystemDisplayId, ticketId }, { dispatch, queryFulfilled }) {
  try {
    const { data } = await queryFulfilled
    dispatch(
      api.util.updateQueryData(
        'fetchTicket',
        { ticketingSystemDisplayId, ticketId },
        (draft) => {
          draft.ticket.subscribed = data.subscribed
        }
      )
    )
  } catch {}
}

const extendedApi = enhancedApi.injectEndpoints({
  endpoints: (builder) => ({

    // TICKETS

    fetchTickets: builder.query({
      query: (params) => {
        return ({
          url: `/ticketing_systems/${params.ticketingSystemDisplayId}/tickets`,
          params: {
            ...(params.searchValue && { search: params.searchValue }),
            ...(params.statusFilters && { statusFilters: params.statusFilters }),
            ...(params.userFilters && { userFilters: params.userFilters }),
            ...(params.sortField && { sortField: params.sortField }),
            ...(params.sortDir && { sortDir: params.sortDir }),
            ...(params.providerTicketBoardId && { providerTicketBoardId: params.providerTicketBoardId }),
            page: params.page,
          },
        })
      },
      transformResponse: (response) => {
        response['partnerStatusesMap'] = getPartnerStatusesMap(response)
        return response
      },
      providesTags: (result, error, args) => {
        const tags = [{ type: ticketingSystemTicketType, id: 'LIST' }]
        // add tags for each ticket filter so it is easy to invalidate them on filter change
        if (args.userFilters) {
          args.userFilters.forEach(filterId => tags.push({
            type: ticketingSystemTicketType,
            id: `TICKET_FILTER_ID_${filterId}`,
          }))
        }
        return tags
      },
    }),

    refetchTickets: builder.mutation({
      query: ({ ticketingSystemDisplayId }) => {
        return ({
          url: `/admin/ticketing_systems/${ticketingSystemDisplayId}/refetch_tickets`,
          method: 'GET',
        })
      },
      invalidatesTags: [{ type: ticketingSystemTicketType, id: 'LIST' }],
    }),

    // TICKET VIEW

    quickSearch: builder.query({
      query: ({ ticketingSystemDisplayId, ...params }) => {
        return ({
          url: `/ticketing_systems/${ticketingSystemDisplayId}/tickets/quick_search`,
          params: {
            ...(params.currentTicketNumber && { currentTicketNumber: params.currentTicketNumber }),
            ...(params.canBeChild && { canBeChild: params.canBeChild }),
            ...(params.canBeParent && { canBeParent: params.canBeParent }),
            ...(params.currentType && { currentType: params.currentType }),
            ...(params.perPage && { perPage: params.perPage }),
            searchTerm: params.searchTerm,
          },
        })
      },
    }),

    fetchTicket: builder.query({
      query: ({ ticketingSystemDisplayId, ticketId }) => {
        return ({
          url: `/ticketing_systems/${ticketingSystemDisplayId}/tickets/${ticketId}`,
        })
      },
      providesTags: (result) => [{ type: ticketingSystemTicketType, id: 'CURRENT' }],
    }),

    // reuses tickets endpoint with different parameters and post-processing
    fetchTicketChildren: builder.query({
      query: ({ ticketingSystemDisplayId, parentTicketId, ...params }) => {
        return ({
          url: `/ticketing_systems/${ticketingSystemDisplayId}/tickets`,
          params: {
            ...(params.searchValue && { search: params.searchValue }),
            ...(params.statusFilters && { statusFilters: params.statusFilters }),
            ...(params.sortField && { sortField: params.sortField }),
            ...(params.sortDir && { sortDir: params.sortDir }),
            ...(params.perPage && { perPage: params.perPage }),
            parentTicketId: parentTicketId,
            page: params.page,
          },
        })
      },
      transformResponse: (response) => {
        response['partnerStatusesMap'] = getPartnerStatusesMap(response)
        return response
      },
      providesTags: (result) => [{ type: 'ticketChildren', id: 'LIST' }],
    }),

    fetchTicketAttachments: builder.query({
      query: (params) => {
        const { ticketingSystemDisplayId, ticketId } = params
        return ({
          url: `/ticketing_systems/${ticketingSystemDisplayId}/tickets/${ticketId}/attachments`,
          params: {
            ...(params.searchValue && { search: params.searchValue }),
            ...(params.partners && { partners: params.partners }),
            ...(params.extensions && { extensions: params.extensions }),
            ...(params.sortField && { sortField: params.sortField }),
            ...(params.sortDir && { sortDir: params.sortDir }),
            ...(params.perPage && { perPage: params.perPage }),
            ...(params.attachmentId && { attachmentId: params.attachmentId }),
            page: params.page,
          },
        })
      },
      providesTags: (result) => [{ type: 'ticketingSystemTicketAttachments', id: 'LIST' }],
    }),

    fetchTicketComments: builder.query({
      query: (params) => {
        const { ticketingSystemDisplayId, ticketId } = params
        return ({
          url: `/ticketing_systems/${ticketingSystemDisplayId}/tickets/${ticketId}/comments`,
          params: {
            ...(params.searchValue && { search: params.searchValue }),
            ...(params.partners && { partners: params.partners }),
            ...(params.authors && { authors: params.authors }),
            ...(params.sortDir && { sortDir: params.sortDir }),
            ...(params.perPage && { perPage: params.perPage }),
            ...(params.commentId && { commentId: params.commentId }),
            page: params.page,
          },
        })
      },
      providesTags: (result) => [{ type: 'ticketingSystemTicketComments', id: 'LIST' }],
    }),

    createTicketComment: builder.mutation({
      query: ({ ticketingSystemDisplayId, ticketId, values }) => {
        return ({
          url: `/ticketing_systems/${ticketingSystemDisplayId}/tickets/${ticketId}/comments`,
          method: 'POST',
          body: { comment: { body: values } },
        })
      },
      onQueryStarted: manuallyUpdateTicketCache,
      invalidatesTags: [{ type: 'ticketingSystemTicketComments', id: 'LIST' }],
    }),

    uploadAttachment: builder.mutation({
      async queryFn (
        { ticketingSystemDisplayId, ticketId, fileInfo }, // args
        { dispatch },
      ) {
        const onUploadProgress = (event, fileInfo) => {
          dispatch(updateAttachmentUploadProgress({
            uploadTotalBytes: event.total,
            uploadCurrentBytes: event.loaded,
            uploadFileName: fileInfo.name,
          }))
        }

        const result = await uploadWithProgressQuery({
          url: `/ticketing_systems/${ticketingSystemDisplayId}/tickets/${ticketId}/attachments`,
          body: fileInfo,
          bodyParamName: 'attachments[]',
          onUploadProgress: onUploadProgress,
          uploadAbortCallback: uploadAborter.uploadAbortCallback,
        })

        // manually update attachments in ticket cache to prevent additional API fetch request
        if (result.data && !result.data.error) {
          dispatch(
            api.util.updateQueryData(
              'fetchTicket',
              { ticketingSystemDisplayId, ticketId },
              () => result.data
            )
          )
        }

        return result
      },
      invalidatesTags: [
        { type: 'ticketingSystemTicketAttachments', id: 'LIST' },
        { type: 'ticketingSystemTicketComments', id: 'LIST' },
      ],
    }),

    uploadAttachmentAbort: builder.mutation({
      queryFn: (_args, { dispatch }) => {
        uploadAborter.abortUpload()
        dispatch(abortAttachmentUpload())
        return { data: null }
      },
    }),

    updateTicketStatus: builder.mutation({
      query: ({ ticketingSystemDisplayId, ticketId, status }) => {
        return ({
          url: `/ticketing_systems/${ticketingSystemDisplayId}/tickets/${ticketId}/statuses`,
          method: 'PATCH',
          body: { status: status },
        })
      },
      onQueryStarted: manuallyUpdateTicketCache,
    }),

    refetchTicket: builder.mutation({
      query: ({ ticketingSystemDisplayId, ticketId }) => {
        return ({
          url: `/admin/ticketing_systems/${ticketingSystemDisplayId}/tickets/${ticketId}/refetch_ticket`,
          method: 'GET',
        })
      },
      onQueryStarted: manuallyUpdateTicketCache,
      invalidatesTags: [
        { type: 'ticketChildren', id: 'LIST' },
        { type: 'ticketingSystemTicketAttachments', id: 'LIST' },
        { type: 'ticketingSystemTicketComments', id: 'LIST' },
      ],
    }),

    // TICKET CREATE / CLONE / EDIT

    fetchTicketForCreate: builder.query({
      query: ({ ticketingSystemDisplayId }) => {
        return ({
          url: `/ticketing_systems/${ticketingSystemDisplayId}/tickets/new`,
        })
      },
    }),

    fetchTicketForCreateNewChild: builder.query({
      query: ({ ticketingSystemDisplayId, parentTicketId }) => {
        return ({
          url: `/ticketing_systems/${ticketingSystemDisplayId}/tickets/${parentTicketId}/new_child`,
        })
      },
    }),

    createTicket: builder.mutation({
      query: ({ ticketingSystemDisplayId, ticketParams }) => {
        return ({
          url: `/ticketing_systems/${ticketingSystemDisplayId}/tickets`,
          method: 'POST',
          body: ticketParams,
        })
      },
    }),

    fetchTicketForClone: builder.query({
      query: ({ ticketingSystemDisplayId, ticketId }) => {
        return ({
          url: `/ticketing_systems/${ticketingSystemDisplayId}/tickets/${ticketId}/clone`,
        })
      },
    }),

    fetchTicketForEdit: builder.query({
      query: ({ ticketingSystemDisplayId, ticketId }) => {
        return ({
          url: `/ticketing_systems/${ticketingSystemDisplayId}/tickets/${ticketId}/edit`,
        })
      },
    }),

    updateTicket: builder.mutation({
      query: ({ ticketingSystemDisplayId, ticketId, ticketParams }) => {
        return ({
          url: `/ticketing_systems/${ticketingSystemDisplayId}/tickets/${ticketId}`,
          method: 'PATCH',
          body: ticketParams,
        })
      },
      onQueryStarted: manuallyUpdateTicketCache,
    }),

    // move children between parents
    moveChildren: builder.mutation({
      query: ({ ticketingSystemDisplayId, parentTicketId, childTicketIds }) => {
        return ({
          url: `/ticketing_systems/${ticketingSystemDisplayId}/tickets/${parentTicketId}/move_children`,
          method: 'POST',
          body: {
            children_ticket_numbers: childTicketIds,
          },
        })
      },
      invalidatesTags: [
        { type: ticketingSystemTicketType, id: 'CURRENT' },
        { type: 'ticketChildren', id: 'LIST' },
      ],
    }),

  }),
  overrideExisting: false,
})

export const {
  useFetchTicketsQuery,
  useLazyFetchTicketsQuery,
  useRefetchTicketsMutation,
  useLazyQuickSearchQuery,
  useFetchTicketQuery,
  useFetchTicketChildrenQuery,
  useFetchTicketAttachmentsQuery,
  useFetchTicketCommentsQuery,
  useCreateTicketCommentMutation,
  useUploadAttachmentMutation,
  useUploadAttachmentAbortMutation,
  useUpdateTicketStatusMutation,
  useRefetchTicketMutation,
  useFetchTicketForCreateQuery,
  useFetchTicketForCreateNewChildQuery,
  useCreateTicketMutation,
  useFetchTicketForCloneQuery,
  useFetchTicketForEditQuery,
  useUpdateTicketMutation,
  useMoveChildrenMutation,
} = extendedApi

export const {
  uploadAttachment,
  updateTicketStatus,
  refetchTicket,
  quickSearch,
  fetchTickets,
} = extendedApi.endpoints
